// prop-type is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import { Box, Card, Switch, Grid } from "@mui/material";
import Divider from '@material-ui/core/Divider';

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";
import SoftSelect from "components/SoftComponents/SoftSelect";
import SoftInput from "components/SoftComponents/SoftInput";
import SoftButton from "components/SoftComponents/SoftButton";

// NewUser page components
import FormField from "modules/inspections/components/FormField";
import { useState } from "react";
import { SyncLoader } from 'react-spinners';

function DeviceConditions({ formData, deviceLayout }) {
    const {
        values, touched, errors, formField, isSubmitting,
        functions: { setDeviceOptions, setFieldValue, handleChange, handleBack, handleCancel },
        custom: { activeStep, isLastStep, user, all_devices, deviceOptions }
    } = formData;
    const { devicePrimary, inspection_type } = formField;

    const [works, setWorks] = useState(
        values?.['devicePrimary-conditions-vandalised'] ||
        values?.['devicePrimary-conditions-damaged'] ||
        values?.['devicePrimary-conditions-audio'] ||
        values?.['devicePrimary-conditions-power'] ||
        values?.['devicePrimary-conditions-frozen'] ||
        values?.['devicePrimary-conditions-location'] ||
        values?.['devicePrimary-conditions-other'].length > 0
    );
    const [conditionsOther, setConditionsOther] = useState(values?.['devicePrimary-conditions-other'] || "");

    function resetConditions() {
        setFieldValue(devicePrimary.conditions.vandalised.name, false);
        setFieldValue(devicePrimary.conditions.damaged.name, false);
        setFieldValue(devicePrimary.conditions.audio.name, false);
        setFieldValue(devicePrimary.conditions.power.name, false);
        setFieldValue(devicePrimary.conditions.frozen.name, false);
        setFieldValue(devicePrimary.conditions.location.name, false);
        setFieldValue(devicePrimary.conditions.other.name, "");
    }

    const conditionItem = (valueKey, title) => {
        var color = values?.[`devicePrimary-conditions-${valueKey}`] ? 'secondary' : 'light';

        return (
            <Grid item xxs={6}>
                <SoftBox display="flex" justifyContent="center" py={3} px={1}
                    onClick={(e) => {
                        console.log(e);
                        setFieldValue(devicePrimary.conditions[valueKey].name, !values[`devicePrimary-conditions-${valueKey}`]);
                    }}
                    sx={({ palette: { white, gradients }, functions: { rgba, linearGradient } }) => ({
                        borderRadius: '10px',
                        background: linearGradient(rgba(gradients[color].main, 0.6), rgba(gradients[color].state, 0.6)),
                        border: '1px solid #aaa'
                    })}
                >
                    <SoftBox display="flex" alignItems="center" sx={{ paddingTop: '2.5px' }}>
                        <SoftTypography variant={deviceLayout == "mobile" ? "mobileBody1" : "body2"} fontWeight={deviceLayout == "mobile" ? 'bold' : 'bold'}>{title}</SoftTypography>
                    </SoftBox>
                </SoftBox>
            </Grid>
        )
    };

    return (
        <SoftBox>
            <SoftBox>
                <SoftBox mb={3} display="flex" flexDirection="column" alignItems="center">
                    <SoftTypography variant="h4" fontWeight="bold">
                        Device Conditions
                    </SoftTypography>
                    <SoftTypography pt={1} variant="mobileBody1" textAlign="center">
                        Is the device running in a perfect, working order?
                    </SoftTypography>
                </SoftBox>
                <SoftBox mt={3}>
                    <Grid container spacing={1}>
                        <Grid item xxs={1} md={3} xl={4} />
                        <Grid item xxs={5} md={3} xl={2}>
                            <SoftButton color={works ? "secondary" : "dark"} variant="gradient" size="medium" sx={{ width: '100% !important' }} onClick={(e) => { setWorks(true); setFieldValue(inspection_type.name, "New Inspection") }}>
                                No
                            </SoftButton>
                        </Grid>
                        <Grid item xxs={5} md={3} xl={2}>
                            <SoftButton color="dark" variant="gradient" size="medium" sx={{ width: '100% !important' }} onClick={(e) => { setFieldValue(inspection_type.name, "General Inspection"); resetConditions(); }} disabled={isSubmitting} type="submit">
                                Yes
                            </SoftButton>
                        </Grid>
                        <Grid item xxs={1} md={3} xl={4} />
                    </Grid>
                </SoftBox>

                {works && (
                    <SoftBox mt={3}>
                        <Divider />
                        <SoftBox mt={2}>
                            <Box display="flex" flex={1} flexDirection={{ xxs: 'column', xl: 'row' }}>
                                <Box flex={{ xxs: 12, xl: 6 }}>
                                    <SoftBox pb={1}>
                                        <SoftTypography variant={deviceLayout == "mobile" ? "mobileBody1" : "subtitle2"} fontWeight={deviceLayout == "mobile" ? "bold" : "regular"}>
                                            Please select the concerning device conditions, or select 'Other' to input another condition.
                                        </SoftTypography>
                                    </SoftBox>
                                    <Grid container spacing={1}>
                                        {conditionItem('vandalised', 'Vandalised')}
                                        {conditionItem('damaged', 'Damaged')}
                                        {conditionItem('audio', 'Audio Issue')}
                                        {conditionItem('power', 'Power Issue')}
                                        {conditionItem('frozen', 'Frozen/Unresponsive')}
                                        {conditionItem('location', 'Location Issue')}
                                        <Grid item xxs={12}>
                                            <SoftBox>
                                                <SoftBox display="flex" justifyContent="center" py={2} px={2}
                                                    onClick={(e) => {
                                                        setConditionsOther(!conditionsOther);
                                                    }}
                                                    sx={({ palette: { white, gradients }, functions: { rgba, linearGradient } }) => ({
                                                        borderRadius: '10px',
                                                        background: linearGradient(rgba(gradients[(values?.[`devicePrimary-conditions-other`] ? 'secondary' : 'light')].main, 0.6), rgba(gradients[(values?.[`devicePrimary-conditions-other`] ? 'secondary' : 'light')].state, 0.6)),
                                                        border: '1px solid #aaa'
                                                    })}
                                                >
                                                    <SoftBox display="flex" alignItems="center" sx={{ paddingTop: '2.5px' }}>
                                                        <SoftTypography variant={deviceLayout == "mobile" ? "mobileBody1" : "body2"} fontWeight={'bold'}>Other</SoftTypography>
                                                    </SoftBox>
                                                </SoftBox>
                                                {conditionsOther && (
                                                    <SoftBox>
                                                        <FormField
                                                            type={devicePrimary.conditions.other.type}
                                                            label={devicePrimary.conditions.other.label}
                                                            name={devicePrimary.conditions.other.name}
                                                            value={values['devicePrimary-conditions-other']}
                                                            placeholder={devicePrimary.conditions.other.placeholder}
                                                            // error={values['devicePrimary-conditions-other'].length < 1}
                                                            // success={values['devicePrimary-conditions-other'].length > 0 && !errors['devicePrimary-conditions-other']}
                                                            onChange={handleChange}
                                                        />
                                                    </SoftBox>
                                                )}
                                            </SoftBox>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Box>
                        </SoftBox>
                    </SoftBox>
                )}
            </SoftBox>
            <SoftBox mt={5} width="100%" display="flex" justifyContent="space-between">
                <SoftButton variant="gradient" color={activeStep === 0 ? "error" : "light"} onClick={activeStep === 0 ? handleCancel : handleBack}>
                    {activeStep === 0 ? "cancel" : "back"}
                </SoftButton>
                {works && (
                    <SoftButton
                        disabled={isSubmitting}
                        type="submit"
                        variant="gradient"
                        color="dark"
                    >
                        {isLastStep ? "submit" : "next"}
                    </SoftButton>
                )}
            </SoftBox>
        </SoftBox>
    );
}

// typechecking props for DeviceConditions
DeviceConditions.propTypes = {
    formData: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).isRequired,
    deviceLayout: PropTypes.string,
};

export default DeviceConditions;
